import React, { useState } from 'react';
import styled from 'styled-components';

interface Preset {
  label: string;
  days: number;
}

interface ReminderDateTimePickerProps {
  value: string | null;
  onChange: (isoDateTime: string | null) => void;
  presets?: Preset[];
  defaultTime?: string;
  error?: string;
}

const DEFAULT_PRESETS: Preset[] = [
  { label: 'מחר', days: 1 },
  { label: 'בעוד יומיים', days: 2 },
];

const DEFAULT_TIME = '09:00';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const QuickOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const QuickButton = styled.button<{ $active?: boolean }>`
  padding: 6px 14px;
  border: 1px solid ${props => (props.$active ? 'var(--primary-color)' : 'var(--border-color)')};
  border-radius: var(--border-radius-sm);
  background: ${props => (props.$active ? 'var(--primary-light)' : 'var(--surface-color)')};
  color: ${props => (props.$active ? 'var(--primary-color)' : 'var(--text-primary)')};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--primary-color);
    color: var(--primary-color);
  }
`;

const ClearButton = styled.button`
  padding: 6px 14px;
  border: 1px dashed var(--border-color);
  border-radius: var(--border-radius-sm);
  background: transparent;
  color: var(--text-secondary);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--error-color);
    color: var(--error-color);
  }
`;

const InputsRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Input = styled.input<{ hasError?: boolean }>`
  padding: 10px 12px;
  border: 1px solid ${props => (props.hasError ? 'var(--error-color)' : 'var(--border-color)')};
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => (props.hasError ? 'var(--error-color)' : 'var(--primary-color)')};
    box-shadow: 0 0 0 2px ${props => (props.hasError ? 'var(--error-light)' : 'var(--primary-light)')};
  }
`;

const ErrorText = styled.span`
  font-size: 12px;
  color: var(--error-color);
`;

const pad = (n: number): string => String(n).padStart(2, '0');

const toDateStr = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Split an ISO datetime into local date (yyyy-mm-dd) and time (HH:mm) strings
const parseIso = (iso: string | null): { date: string; time: string } => {
  if (!iso) return { date: '', time: '' };
  const d = new Date(iso);
  if (isNaN(d.getTime())) return { date: '', time: '' };
  return { date: toDateStr(d), time: `${pad(d.getHours())}:${pad(d.getMinutes())}` };
};

/**
 * בורר תזכורת אופציונלי לשימוש חוזר: כפתורים מהירים (מחר / בעוד יומיים)
 * + בחירת תאריך ושעה חופשיים. פולט ISO datetime (או null אם לא נבחר מועד).
 */
const ReminderDateTimePicker: React.FC<ReminderDateTimePickerProps> = ({
  value,
  onChange,
  presets = DEFAULT_PRESETS,
  defaultTime = DEFAULT_TIME,
  error,
}) => {
  const initial = parseIso(value);
  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);
  const [activeDays, setActiveDays] = useState<number | null>(null);

  // Emit a raw local datetime string ("YYYY-MM-DDTHH:mm", no Z), or null when no
  // full date is chosen. Matches the system convention (appointments / CreateReminderModal);
  // toISOString() would shift to UTC and be stored/displayed 2-3 hours off.
  const emit = (d: string, t: string) => {
    onChange(d && t ? `${d}T${t}` : null);
  };

  const applyPreset = (days: number) => {
    const target = new Date();
    target.setDate(target.getDate() + days);
    const d = toDateStr(target);
    setDate(d);
    setTime(defaultTime);
    setActiveDays(days);
    emit(d, defaultTime);
  };

  const handleDateChange = (d: string) => {
    const t = time || defaultTime;
    setDate(d);
    setActiveDays(null);
    if (d && !time) setTime(defaultTime);
    emit(d, t);
  };

  const handleTimeChange = (t: string) => {
    setTime(t);
    setActiveDays(null);
    emit(date, t);
  };

  const handleClear = () => {
    setDate('');
    setTime('');
    setActiveDays(null);
    onChange(null);
  };

  const today = toDateStr(new Date());

  return (
    <Container>
      <QuickOptions>
        {presets.map(p => (
          <QuickButton
            key={p.days}
            type="button"
            $active={activeDays === p.days}
            onClick={() => applyPreset(p.days)}
          >
            {p.label}
          </QuickButton>
        ))}
        {date && (
          <ClearButton type="button" onClick={handleClear}>
            בטל תזכורת
          </ClearButton>
        )}
      </QuickOptions>

      <InputsRow>
        <Input
          type="date"
          value={date}
          min={today}
          onChange={e => handleDateChange(e.target.value)}
          hasError={!!error}
          aria-label="תאריך תזכורת"
        />
        <Input
          type="time"
          value={time}
          onChange={e => handleTimeChange(e.target.value)}
          hasError={!!error}
          aria-label="שעת תזכורת"
        />
      </InputsRow>

      {error && <ErrorText>{error}</ErrorText>}
    </Container>
  );
};

export default ReminderDateTimePicker;
